import React, { useEffect, useState } from "react";
import axios from "axios";
import { Gift, CheckCircle2 } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

/**
 * Polls the backend for Brother Gift Card promo status and renders one of:
 *   - eligible   →  green "You qualify!" badge
 *   - so close   →  amber progress nudge ("Add 1 more toner to qualify")
 *   - hidden     →  nothing (cart has no Brother items at all)
 */
export default function GiftCardPromoBanner() {
  const { user } = useAuth();
  const { cart } = useCart();
  const [promo, setPromo] = useState(null);

  // Refetch whenever the cart contents change
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    axios
      .get(`${API}/cart/promo`)
      .then((res) => !cancelled && setPromo(res.data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user, cart?.items?.length, cart?.total]);

  if (!user || !promo) return null;
  const {
    eligible, promo_type, gift_card_value,
    toner_qty, drum_qty, toners_needed, drums_needed,
    subtotal, spend_threshold, amount_to_spend,
  } = promo;

  // Hide only if neither tier is in play yet
  const hasBrotherProgress = toner_qty > 0 || drum_qty > 0;
  const closeToSpend = subtotal > 50 && amount_to_spend > 0 && amount_to_spend <= 100;
  if (!eligible && !hasBrotherProgress && !closeToSpend) return null;

  if (eligible) {
    const isBrother = promo_type === "brother";
    return (
      <div
        className="rounded-xl shadow-lg p-5 mb-6 flex items-start gap-4 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #16a34a 0%, #00a99d 100%)" }}
        data-testid="gift-card-eligible-banner"
      >
        <div className="absolute -right-6 -top-6 text-white/10">
          <Gift className="w-32 h-32" />
        </div>
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <div className="relative">
          <p className="text-xs uppercase tracking-wider font-bold text-white/90 mb-1">You qualify!</p>
          <h3 className="text-xl md:text-2xl font-extrabold leading-tight">
            FREE £{Number(gift_card_value || (isBrother ? 50 : 25)).toFixed(0)} Gift Card on its way 🎁
          </h3>
          <p className="text-sm text-white/90 mt-1">
            {isBrother
              ? "Brother bundle bonus — choose any of 70+ retailers. We'll confirm your preference after checkout."
              : "Thank-you bonus for spending over £300. Reply to your order confirmation with your preferred brand (e.g. Tesco), name & delivery address."}
          </p>
        </div>
      </div>
    );
  }

  // Almost-there nudge — Brother variant
  if (hasBrotherProgress) {
    const need = [];
    if (toners_needed > 0)
      need.push(`${toners_needed} more Brother toner${toners_needed === 1 ? "" : "s"}`);
    if (drums_needed > 0)
      need.push(`${drums_needed} more Brother drum${drums_needed === 1 ? "" : "s"}`);

    return (
      <div
        className="rounded-xl shadow p-5 mb-6 flex items-start gap-4 border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50"
        data-testid="gift-card-progress-banner"
      >
        <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0">
          <Gift className="w-6 h-6 text-amber-900" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider font-bold text-amber-700 mb-1">So close!</p>
          <h3 className="text-base md:text-lg font-bold text-gray-900 leading-tight">
            Add {need.join(" + ")} for a FREE £50 Pre-Paid Gift Card
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            You have {toner_qty} toner{toner_qty === 1 ? "" : "s"} and {drum_qty} drum{drum_qty === 1 ? "" : "s"} in your basket.
            Need 2 of each to qualify.
          </p>
        </div>
      </div>
    );
  }

  // Spend-tier nudge — within £100 of £300 threshold
  return (
    <div
      className="rounded-xl shadow p-5 mb-6 flex items-start gap-4 border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50"
      data-testid="gift-card-spend-progress-banner"
    >
      <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0">
        <Gift className="w-6 h-6 text-amber-900" />
      </div>
      <div className="flex-1">
        <p className="text-xs uppercase tracking-wider font-bold text-amber-700 mb-1">Almost there!</p>
        <h3 className="text-base md:text-lg font-bold text-gray-900 leading-tight">
          Add &pound;{amount_to_spend.toFixed(2)} more to unlock a FREE &pound;25 thank-you Gift Card
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          Orders over &pound;{Number(spend_threshold).toFixed(0)} (excl. VAT) qualify.
          Reply to your order email with your preferred retailer (Tesco, Amazon, John Lewis…) to claim.
        </p>
      </div>
    </div>
  );
}
